/**
 * MergeConfirmationModal - Unified N-to-1 merge modal for all entity types
 * 
 * Layout: 50/50 split
 * - Left: Vertical stack of collapsible source item cards with checkboxes
 * - Right: Editable form for the merged item
 * 
 * Features:
 * - Supports any number of source items (N ≥ 2)
 * - Uses shared ENTITY_CONFIG for consistent field rendering
 * - Fetches source items from DB in real-time
 * - Handles relationship re-pointing on backend
 * - Transaction-safe: blocks deletion until merge succeeds
 * - Orchestrated data loading with loading spinner
 * - Entity ID resolution for ref fields
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  ChevronDown,
  ChevronRight,
  GitMerge,
  Loader2,
  X,
  AlertCircle,
  Check,
} from 'lucide-react';
import { collectionActionsApi, epicsApi } from '../../services/api';
import { toast } from 'sonner';
import { getFieldLabel } from './utils/fieldLabels';
import { DatePickerWithMarkers } from '../ui/date-picker-with-markers';
import { 
  resolveEntityTitle, 
  isSingleRefField,
  isMultiRefField,
  getRefFieldOptions,
  getEntityIdField,
  getEntityConfig,
  TEXTAREA_FIELDS,
} from './utils/entityResolution';
import CopilotMarkdown from './CopilotMarkdown';

// Entity-specific merge panels
import GoalMergePanel from './GoalMergePanel';
import InitiativeMergePanel from './InitiativeMergePanel';
import EpicMergePanel from './EpicMergePanel';
import FeatureMergePanel from './FeatureMergePanel';
import PersonaMergePanel from './PersonaMergePanel';
import RequestMergePanel from './RequestMergePanel';

// Map of entity types that have custom merge panels
const ENTITY_SPECIFIC_PANELS = {
  goal: GoalMergePanel,
  initiative: InitiativeMergePanel,
  epic: EpicMergePanel,
  feature: FeatureMergePanel,
  persona: PersonaMergePanel,
  request: RequestMergePanel,
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Get display value for a field, handling nested objects, arrays, and entity resolution
 */
const getDisplayValue = (item, fieldName, fieldSchema = {}, linkableEntities = {}) => {
  if (!item) return '';
  
  // Handle nested request_data.priority_level
  if (fieldName === 'priority_level' && item.request_data?.priority_level) {
    return item.request_data.priority_level;
  }
  
  const value = item[fieldName];
  if (value === null || value === undefined) return '';
  
  // Check if this is a ref field that needs resolution
  const fieldConfig = fieldSchema[fieldName];
  if (fieldConfig?.ref_entity) {
    const refType = fieldConfig.ref_entity;
    
    // Handle array of refs (persona_ids, tag_ids)
    if (Array.isArray(value)) {
      if (value.length === 0) return 'None';
      const resolved = value.map(id => resolveEntityTitle(id, refType, linkableEntities));
      return resolved.join(', ');
    }
    
    // Handle single ref (epic_id, owner_id)
    return resolveEntityTitle(value, refType, linkableEntities);
  }
  
  if (Array.isArray(value)) {
    // Handle requirements array
    if (fieldName === 'requirements') {
      return value.map(r => typeof r === 'object' ? r.text : r).filter(Boolean).join('\n');
    }
    return value.join(', ');
  }
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

// =============================================================================
// SOURCE ITEM CARD COMPONENT
// =============================================================================

const SourceItemCard = ({ 
  item, 
  entityType, 
  isSelected, 
  isExpanded, 
  onToggleSelect, 
  onToggleExpand,
  index,
  fieldSchema = {},
  linkableEntities = {},
}) => {
  const config = getEntityConfig(entityType);
  const primaryValue = getDisplayValue(item, config.primaryField, fieldSchema, linkableEntities);
  
  return (
    <div 
      className={cn(
        "border rounded-lg transition-all",
        isSelected 
          ? "border-indigo-300 dark:border-indigo-600 bg-indigo-50/50 dark:bg-indigo-900/20" 
          : "border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 opacity-60"
      )}
      data-testid={`merge-source-item-${index}`}
    >
      {/* Header - always visible */}
      <div 
        className="flex items-center gap-3 p-3 cursor-pointer"
        onClick={() => onToggleExpand()}
      >
        <Checkbox
          checked={isSelected}
          onCheckedChange={() => onToggleSelect()}
          onClick={(e) => e.stopPropagation()}
          data-testid={`merge-source-checkbox-${index}`}
        />
        
        <button
          type="button"
          className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand();
          }}
        >
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 text-gray-500" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-500" />
          )}
        </button>
        
        <div className="flex-1 min-w-0">
          <p className={cn(
            "font-medium truncate",
            isSelected ? "text-gray-900 dark:text-gray-100" : "text-gray-500 dark:text-gray-400"
          )}>
            {primaryValue || `${entityType} ${index + 1}`}
          </p>
        </div>
        
        <Badge variant="outline" className="text-xs shrink-0">
          Source {index + 1}
        </Badge>
      </div>
      
      {/* Expanded content */}
      {isExpanded && (
        <div className="px-3 pb-3 pt-0 border-t border-gray-100 dark:border-gray-700/50">
          <div className="space-y-3 mt-3">
            {config.displayFields.map(fieldName => {
              if (fieldName === config.primaryField) return null; // Already shown in header
              const value = getDisplayValue(item, fieldName, fieldSchema, linkableEntities);
              if (!value) return null;
              
              const label = config.fieldLabels[fieldName] || getFieldLabel(entityType, fieldName);
              const isLongText = TEXTAREA_FIELDS.has(fieldName);
              
              return (
                <div key={fieldName} className="space-y-1">
                  <Label className="text-xs text-gray-500 dark:text-gray-400">
                    {label}
                  </Label>
                  {isLongText ? (
                    <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap bg-white dark:bg-gray-800 p-2 rounded border border-gray-100 dark:border-gray-700 max-h-32 overflow-y-auto">
                      <CopilotMarkdown content={value} />
                    </div>
                  ) : (
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      {value}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// MERGED ITEM FORM COMPONENT
// =============================================================================

const MergedItemForm = ({ 
  entityType, 
  formData, 
  onChange,
  errors,
  fieldSchema = {},  // Field schema from backend (source of truth)
  linkableEntities = {},  // Linkable entities for ref field dropdowns
}) => {
  const config = getEntityConfig(entityType);
  
  // Determine if a field is required (from schema)
  const isRequired = (fieldName) => fieldSchema[fieldName]?.required || false;
  
  // Get label from schema or fallback to config
  const getLabel = (fieldName) => {
    return fieldSchema[fieldName]?.label || 
           config.fieldLabels[fieldName] || 
           getFieldLabel(entityType, fieldName);
  };
  
  // Get enum values from schema if available
  const getEnumValues = (fieldName) => fieldSchema[fieldName]?.values || null;
  
  const renderField = (fieldName) => {
    const label = getLabel(fieldName);
    const required = isRequired(fieldName);
    const value = formData[fieldName] || '';
    const error = errors?.[fieldName];
    const schemaType = fieldSchema[fieldName]?.type;
    const enumValues = getEnumValues(fieldName);
    const fieldConfig = fieldSchema[fieldName];
    
    // Render required indicator
    const labelWithRequired = (
      <Label className="text-sm font-medium">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </Label>
    );
    
    // Single ref field (epic_id, owner_id, assignee_id, initiative_id)
    if (isSingleRefField(fieldConfig)) {
      const options = getRefFieldOptions(fieldConfig.ref_entity, linkableEntities);
      const NONE_SENTINEL = '__none__';
      
      return (
        <div key={fieldName} className="space-y-1.5">
          {labelWithRequired}
          <Select
            value={value || (required ? '' : NONE_SENTINEL)}
            onValueChange={(v) => onChange(fieldName, v === NONE_SENTINEL ? '' : v)}
          >
            <SelectTrigger className={cn(error && "border-red-500")}>
              <SelectValue placeholder={`Select ${label.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              {/* Only show "None" option for optional fields */}
              {!required && (
                <SelectItem value={NONE_SENTINEL}>None</SelectItem>
              )}
              {options.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      );
    }
    
    // Multi-ref field (persona_ids, tag_ids)
    if (isMultiRefField(fieldConfig)) {
      const options = getRefFieldOptions(fieldConfig.ref_entity, linkableEntities);
      const selectedValues = Array.isArray(value) ? value : [];
      
      return (
        <div key={fieldName} className="space-y-1.5">
          {labelWithRequired}
          <div className={cn(
            "border rounded-md p-2 min-h-[80px] max-h-[150px] overflow-y-auto",
            error ? "border-red-500" : "border-gray-200 dark:border-gray-700"
          )}>
            {options.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No {label.toLowerCase()} available</p>
            ) : (
              <div className="space-y-1">
                {options.map(opt => (
                  <label 
                    key={opt.value} 
                    className="flex items-center gap-2 p-1 rounded hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                  >
                    <Checkbox
                      checked={selectedValues.includes(opt.value)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          onChange(fieldName, [...selectedValues, opt.value]);
                        } else {
                          onChange(fieldName, selectedValues.filter(v => v !== opt.value));
                        }
                      }}
                    />
                    <span className="text-sm">{opt.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          {selectedValues.length > 0 && (
            <p className="text-xs text-gray-500">{selectedValues.length} selected</p>
          )}
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      );
    }
    
    // If schema provides enum values, render as select
    if (enumValues && enumValues.length > 0) {
      return (
        <div key={fieldName} className="space-y-1.5">
          {labelWithRequired}
          <Select
            value={value}
            onValueChange={(v) => onChange(fieldName, v)}
          >
            <SelectTrigger className={cn(error && "border-red-500")}>
              <SelectValue placeholder={`Select ${label.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              {enumValues.map(opt => (
                <SelectItem key={opt} value={opt}>
                  {opt.charAt(0).toUpperCase() + opt.slice(1).replace(/_/g, ' ')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      );
    }
    
    // Date field (target_date, due_date, etc.) - uses unified calendar with release markers
    if (schemaType === 'date') {
      return (
        <div key={fieldName} className="space-y-1.5">
          {labelWithRequired}
          <DatePickerWithMarkers
            value={value}
            onChange={(date) => onChange(fieldName, date)}
            placeholder={`Select ${label.toLowerCase()}`}
          />
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      );
    }
    
    // Textarea for long text fields (schema type 'text' or known long fields)
    if (schemaType === 'text' || TEXTAREA_FIELDS.has(fieldName)) {
      return (
        <div key={fieldName} className="space-y-1.5">
          {labelWithRequired}
          <Textarea
            value={value}
            onChange={(e) => onChange(fieldName, e.target.value)}
            className={cn("min-h-[100px] resize-y", error && "border-red-500")}
            placeholder={`Enter ${label.toLowerCase()}`}
          />
          {error && <p className="text-xs text-red-500">{error}</p>}
        </div>
      );
    }
    
    // Default: text input
    return (
      <div key={fieldName} className="space-y-1.5">
        {labelWithRequired}
        <Input
          value={value}
          onChange={(e) => onChange(fieldName, e.target.value)}
          className={cn(error && "border-red-500")}
          placeholder={`Enter ${label.toLowerCase()}`}
        />
        {error && <p className="text-xs text-red-500">{error}</p>}
      </div>
    );
  };
  
  // Use fields from schema if available, otherwise fall back to config
  const fieldsToRender = Object.keys(fieldSchema).length > 0
    ? Object.keys(fieldSchema)
    : (config.editableFields || config.displayFields);
  
  return (
    <div className="space-y-4">
      {fieldsToRender.map(fieldName => renderField(fieldName))}
    </div>
  );
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

const MergeConfirmationModal = ({
  isOpen,
  onClose,
  action,
  actionIndex,  // Index in the actions array for individual removal
  productId,
  onMergeComplete,
  onError,
  logActionOutcome,
  user,
  zIndex = 50,  // Z-index for modal stacking
  // Data passed from parent (follows "data flows down" pattern like SplitFeatureModal)
  linkableEntities: propLinkableEntities,  // { epics, releases, personas, tags, teamMembers }
  sourceItemsFromParent,  // Pre-fetched source items (optional - for when parent has them in memory)
}) => {
  // State
  const [sourceItems, setSourceItems] = useState([]);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState(null);
  
  // Selection state for source items
  const [selectedItems, setSelectedItems] = useState({});
  const [expandedItems, setExpandedItems] = useState({});
  
  // Form state for merged item
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  
  // Field schema from backend (source of truth for required fields)
  const [fieldSchema, setFieldSchema] = useState({});
  
  // Use linkable entities from prop (data flows down from parent)
  // This avoids redundant API calls - parent already has this data loaded
  const linkableEntities = propLinkableEntities || {
    epic: [],
    release: [],
    persona: [],
    tag: [],
    user: [],
    goal: [],
    initiative: [],
  };
  
  // Orchestrated loading state (only for source items and schema now)
  const [modalDataLoading, setModalDataLoading] = useState(false);
  const [modalDataReady, setModalDataReady] = useState(false);
  
  // Extract entity type and item IDs from action
  const entityType = action?.entity_type || 'feature';
  const itemIds = action?.item_ids || [];
  const config = getEntityConfig(entityType);
  
  // ==========================================================================
  // ORCHESTRATED DATA LOADING (Simplified - linkableEntities from props)
  // ==========================================================================
  
  const loadAllModalData = useCallback(async () => {
    if (modalDataLoading || !itemIds.length || !productId) return;
    
    setModalDataLoading(true);
    setModalDataReady(false);
    setError(null);
    
    try {
      // ========== STEP 1: Fetch field schema ==========
      const schemaResponse = await collectionActionsApi.getFieldSchema(entityType);
      const schema = schemaResponse.data?.fields || {};
      
      // ========== STEP 2: Get source items ==========
      // Prefer items from parent (in-memory) over API fetch
      let fetchedItems = [];
      
      if (sourceItemsFromParent && sourceItemsFromParent.length > 0) {
        // Use pre-fetched items from parent (no API call needed)
        fetchedItems = sourceItemsFromParent;
      } else if (entityType === 'epic') {
        // Epics: Use /details endpoint for each item to get features + KPIs
        const detailsResults = await Promise.all(
          itemIds.map(id => epicsApi.getDetails(id).catch(() => null))
        );
        fetchedItems = detailsResults.filter(r => r?.data).map(r => r.data);
      } else {
        // Other entities: Use batch fetch
        const itemsResponse = await collectionActionsApi.getStandardEntitiesBatch(
          itemIds.map(id => ({
            product_id: productId,
            item_id: id,
            entity_type: entityType,
          }))
        );
        const resultsDict = itemsResponse.data?.results || {};
        fetchedItems = Object.values(resultsDict);
      }
      
      // ========== STEP 3: Validate source items ==========
      if (fetchedItems.length === 0) {
        setError('Could not find the items to merge. They may have been deleted.');
        setModalDataLoading(false);
        return;
      }
      
      // ========== STEP 4: Initialize selection state ==========
      const initialSelected = {};
      const initialExpanded = {};
      fetchedItems.forEach((_, idx) => {
        initialSelected[idx] = true;
        initialExpanded[idx] = idx === 0; // Expand first item
      });
      
      // ========== STEP 5: Initialize form with AI-suggested values ==========
      // Use linkableEntities from props for resolving display values
      const fieldsToInit = Object.keys(schema).length > 0 
        ? Object.keys(schema) 
        : (config.editableFields || config.displayFields);
      
      const initialForm = {};
      fieldsToInit.forEach(field => {
        const fieldConfig = schema[field];
        
        // Use AI-suggested value if available
        if (action[`merged_content_${field}`] !== undefined) {
          initialForm[field] = action[`merged_content_${field}`];
        } else if (action.merged_content?.[field] !== undefined) {
          initialForm[field] = action.merged_content[field];
        } else if (field === config.primaryField || field === 'title' || field === 'name') {
          // For primary field, use AI title or combine from sources
          initialForm[field] = action.merged_content_title || 
                               action.merged_content?.title ||
                               fetchedItems.map(i => getDisplayValue(i, field, schema, linkableEntities)).filter(Boolean).join(' + ');
        } else if (field === 'description') {
          // Combine descriptions
          initialForm[field] = action.merged_content_description ||
                               action.merged_content?.description ||
                               fetchedItems.map(i => getDisplayValue(i, field, schema, linkableEntities)).filter(Boolean).join('\n\n---\n\n');
        } else if (isSingleRefField(fieldConfig) || isMultiRefField(fieldConfig)) {
          // For ref fields, use first non-empty value (keeping as ID, not resolved name)
          for (const item of fetchedItems) {
            const rawValue = item[field];
            if (rawValue !== null && rawValue !== undefined) {
              initialForm[field] = rawValue;
              break;
            }
          }
        } else {
          // Use first non-empty value from sources
          for (const item of fetchedItems) {
            const value = getDisplayValue(item, field, schema, linkableEntities);
            if (value) {
              initialForm[field] = item[field]; // Use raw value, not display value
              break;
            }
          }
        }
      });
      
      // ========== STEP 6: Set all state atomically ==========
      setFieldSchema(schema);
      setSourceItems(fetchedItems);
      setSelectedItems(initialSelected);
      setExpandedItems(initialExpanded);
      setFormData(initialForm);
      setModalDataReady(true);
      
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load items. Please try again.');
    } finally {
      setModalDataLoading(false);
    }
  }, [itemIds, productId, entityType, action, config, modalDataLoading, sourceItemsFromParent, linkableEntities]);
  
  // Trigger data loading when modal opens
  useEffect(() => {
    if (isOpen && !modalDataReady && !modalDataLoading) {
      loadAllModalData();
    }
  }, [isOpen, modalDataReady, modalDataLoading, loadAllModalData]);
  
  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setModalDataReady(false);
      setSourceItems([]);
      setFieldSchema({});
      setFormData({});
      setFormErrors({});
      setSelectedItems({});
      setExpandedItems({});
      setError(null);
    }
  }, [isOpen]);
  
  // Handle form field change
  const handleFormChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    setFormErrors(prev => ({ ...prev, [field]: null }));
  }, []);
  
  // Toggle source item selection
  const toggleItemSelect = useCallback((idx) => {
    setSelectedItems(prev => ({ ...prev, [idx]: !prev[idx] }));
  }, []);
  
  // Toggle source item expansion
  const toggleItemExpand = useCallback((idx) => {
    setExpandedItems(prev => ({ ...prev, [idx]: !prev[idx] }));
  }, []);
  
  // Validate form using field schema from backend
  const validateForm = useCallback(() => {
    const errors = {};
    
    // Validate all required fields from schema
    for (const [fieldName, fieldConfig] of Object.entries(fieldSchema)) {
      if (fieldConfig.required) {
        const value = formData[fieldName];
        const isEmpty = value === undefined || value === null || 
          (typeof value === 'string' && !value.trim());
        
        if (isEmpty) {
          const label = fieldConfig.label || fieldName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
          errors[fieldName] = `${label} is required`;
        }
      }
    }
    
    // Fallback: if no schema, at least validate primary field
    if (Object.keys(fieldSchema).length === 0) {
      const primaryField = config.primaryField;
      if (!formData[primaryField]?.trim()) {
        errors[primaryField] = `${config.fieldLabels[primaryField] || primaryField} is required`;
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, fieldSchema, config]);
  
  // Get selected item IDs using entity-specific ID field (using shared utility)
  const selectedItemIds = useMemo(() => {
    const idField = getEntityIdField(entityType);
    return sourceItems
      .filter((_, idx) => selectedItems[idx])
      .map(item => item[idField] || item.id || item._id)
      .filter(Boolean); // Remove any undefined/null values
  }, [sourceItems, selectedItems, entityType]);
  
  // Count selected items
  const selectedCount = Object.values(selectedItems).filter(Boolean).length;
  
  // Handle merge execution
  const handleMerge = async () => {
    // Validate at least 2 items selected
    if (selectedCount < 2) {
      toast.error('Please select at least 2 items to merge');
      return;
    }
    
    // Validate form
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setExecuting(true);
    setError(null);
    
    try {
      // Build merged content from form data
      const mergedContent = { ...formData };
      
      // Handle priority_level for requests
      if (entityType === 'request' && formData.priority_level) {
        mergedContent.request_data = { priority_level: formData.priority_level };
        delete mergedContent.priority_level;
      }
      
      const response = await collectionActionsApi.mergeItems({
        product_id: productId,
        entity_type: entityType,
        item_ids: selectedItemIds,
        merged_content: mergedContent,
        parent_id: action.parent_id,
        archive_originals: action.archive_originals !== false,
      });
      
      // Log success
      const primaryValue = formData[config.primaryField] || 'item';
      const summary = `Merged ${selectedCount} ${entityType}s into "${primaryValue}"`;
      
      logActionOutcome?.('accepted', summary, {
        author_id: user?.user_id,
        author_name: user?.name || user?.email || 'You',
      }, { actionIndex });
      
      toast.success(summary);
      onMergeComplete?.(response.data);
      onClose();
      
    } catch (err) {
      console.error('Merge failed:', err);
      
      // Handle various error response formats
      let errorMessage = 'Failed to merge items. Please try again.';
      const detail = err.response?.data?.detail;
      
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        // FastAPI validation error format: [{type, loc, msg, input, url}, ...]
        errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join('; ');
      } else if (detail && typeof detail === 'object') {
        // Single validation error object
        errorMessage = detail.msg || detail.message || JSON.stringify(detail);
      }
      
      setError(errorMessage);
      toast.error(errorMessage);
      onError?.(err);
    } finally {
      setExecuting(false);
    }
  };
  
  // Handle dismiss - just close modal, don't log dismissal
  // (dismissal should only happen via the Dismiss button on the action card)
  const handleDismiss = () => {
    onClose();
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0" style={{ zIndex }}>
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
      />
      
      {/* Modal */}
      <div 
        className="absolute inset-4 md:inset-8 lg:inset-12 bg-white dark:bg-gray-900 rounded-xl shadow-2xl flex flex-col overflow-hidden"
        data-testid="merge-confirmation-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg">
              <GitMerge className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Merge {entityType.charAt(0).toUpperCase() + entityType.slice(1)}s
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Combining {itemIds.length} items into one
              </p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDismiss}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {!modalDataReady ? (
            <div className="flex flex-col items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              <span className="mt-3 text-gray-600 dark:text-gray-400">Loading merge data...</span>
            </div>
          ) : error && sourceItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-4">
              <AlertCircle className="w-12 h-12 text-red-500" />
              <p className="text-gray-600 dark:text-gray-400">{error}</p>
              <Button variant="outline" onClick={handleDismiss}>Close</Button>
            </div>
          ) : ENTITY_SPECIFIC_PANELS[entityType] ? (
            // Use entity-specific panel for supported entity types
            (() => {
              const EntityPanel = ENTITY_SPECIFIC_PANELS[entityType];
              return (
                <EntityPanel
                  sourceItems={sourceItems}
                  selectedItems={selectedItems}
                  expandedItems={expandedItems}
                  onToggleSelect={toggleItemSelect}
                  onToggleExpand={toggleItemExpand}
                  formData={formData}
                  onFormChange={handleFormChange}
                  productId={productId}
                  errors={formErrors}
                  linkableEntities={linkableEntities}
                />
              );
            })()
          ) : (
            // Fallback to generic dynamic panels for other entity types
            <div className="grid grid-cols-2 h-full divide-x divide-gray-200 dark:divide-gray-700">
              {/* Left Panel: Source Items */}
              <div className="flex flex-col overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between">
                    <h3 className="font-medium text-gray-900 dark:text-gray-100">
                      Source Items
                    </h3>
                    <Badge variant="outline">
                      {selectedCount} of {sourceItems.length} selected
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Select items to include in the merge
                  </p>
                </div>
                
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {sourceItems.map((item, idx) => (
                    <SourceItemCard
                      key={item.id || item._id || idx}
                      item={item}
                      entityType={entityType}
                      isSelected={selectedItems[idx]}
                      isExpanded={expandedItems[idx]}
                      onToggleSelect={() => toggleItemSelect(idx)}
                      onToggleExpand={() => toggleItemExpand(idx)}
                      index={idx}
                      fieldSchema={fieldSchema}
                      linkableEntities={linkableEntities}
                    />
                  ))}
                </div>
              </div>
              
              {/* Right Panel: Merged Item Form */}
              <div className="flex flex-col overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="font-medium text-gray-900 dark:text-gray-100">
                    Merged Result
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Edit the combined item before applying
                  </p>
                </div>
                
                <div className="flex-1 overflow-y-auto p-4">
                  <MergedItemForm
                    entityType={entityType}
                    formData={formData}
                    onChange={handleFormChange}
                    errors={formErrors}
                    fieldSchema={fieldSchema}
                    linkableEntities={linkableEntities}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {error && modalDataReady && sourceItems.length > 0 && (
              <span className="text-red-500 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {error}
              </span>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={handleDismiss}
              disabled={executing}
            >
              Cancel
            </Button>
            <Button
              onClick={handleMerge}
              disabled={modalDataLoading || executing || selectedCount < 2}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="merge-apply-btn"
            >
              {executing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Merging...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Merge {selectedCount} Items
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MergeConfirmationModal;
